import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";

// Threshold (px) past which we treat the drag as a "commit": releasing now
// triggers the refresh. Below it the indicator just snaps back.
const COMMIT_THRESHOLD = 70;
// Hard ceiling on how far the indicator visually pulls down — beyond this
// extra movement is dampened to 0 so the user gets a tactile "wall".
const MAX_PULL = 120;
// Damping factor — pull distance is finger-delta divided by this so the
// indicator feels heavy rather than sticking 1:1 to the finger.
const DAMPING = 2;

// Detect "installed as a home-screen PWA" so we only intercept gestures
// where iOS itself wouldn't already provide pull-to-refresh. In a normal
// browser tab the platform's native gesture stays in control.
function isStandalonePWA(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS WebKit exposes this on Safari's WebView when launched from home
    // screen — typed as any because it's not in the standard DOM lib.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/**
 * Custom pull-to-refresh for the standalone PWA. Renders nothing when not
 * running as an installed app — the browser's native gesture covers that
 * case. Otherwise listens to touch events on the document, and when the
 * user drags down from `window.scrollY === 0`, animates a top indicator
 * and triggers `window.location.reload()` past the commit threshold.
 *
 * Lives globally inside AppShell so every authenticated page gets it
 * without per-page wiring. The indicator is `position: fixed`, sitting
 * just below the safe-area inset so it doesn't fight the sticky header.
 */
export function PullToRefresh() {
  const [enabled] = useState<boolean>(() => isStandalonePWA());
  const [pull, setPull] = useState(0);      // current visual pull distance, in px
  const [refreshing, setRefreshing] = useState(false);

  // Refs so the touch handlers don't capture stale React state. We need to
  // know within touchmove whether the gesture started at the top of the
  // page, the original finger position on both axes, whether we've
  // decided to intercept this gesture vs. let the page scroll normally,
  // and whether we've already classified this gesture as horizontal so
  // we don't keep re-checking on every move.
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const intercepting = useRef(false);
  const committedRef = useRef(false);
  const gestureLocked = useRef<null | "horizontal" | "vertical">(null);

  useEffect(() => {
    if (!enabled) return;

    const onTouchStart = (e: TouchEvent) => {
      // Only consider single-finger pulls — pinch/zoom etc. are not our job.
      if (e.touches.length !== 1) return;
      // Only intercept when we're already scrolled to the very top.
      // Otherwise pulling down is just normal scrolling.
      if (window.scrollY > 0) return;
      // Don't fight an in-progress refresh.
      if (refreshing) return;
      startX.current = e.touches[0].clientX;
      startY.current = e.touches[0].clientY;
      intercepting.current = false;
      committedRef.current = false;
      gestureLocked.current = null;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startY.current == null || startX.current == null) return;
      const dx = e.touches[0].clientX - startX.current;
      const dy = e.touches[0].clientY - startY.current;

      // Direction lock: classify the gesture as horizontal vs vertical
      // ONCE, after either axis has moved enough (8 px) to be intentional.
      // If horizontal wins, bail out of PTR entirely so the underlying
      // slider / carousel / etc. gets a clean drag. Without this, a
      // horizontal swipe with even a couple of pixels of natural downward
      // drift would trigger preventDefault below and stall the slider —
      // which is exactly what users reported for the Trikot slider.
      if (gestureLocked.current === null) {
        const absX = Math.abs(dx);
        const absY = Math.abs(dy);
        if (absX < 8 && absY < 8) return; // movement too small to classify
        gestureLocked.current = absX > absY ? "horizontal" : "vertical";
      }
      if (gestureLocked.current === "horizontal") return;

      if (dy <= 0) {
        // Finger moved up or sideways — abandon any pull-state we had.
        if (intercepting.current) {
          intercepting.current = false;
          setPull(0);
        }
        return;
      }
      // First time the delta is positive, decide to intercept.
      if (!intercepting.current) {
        intercepting.current = true;
      }
      const damped = Math.min(MAX_PULL, dy / DAMPING);
      committedRef.current = damped >= COMMIT_THRESHOLD;
      setPull(damped);
      // preventDefault stops iOS rubber-band scrolling from competing.
      // Only do it while we're actively pulling so normal scroll stays
      // crisp.
      if (e.cancelable) e.preventDefault();
    };

    const onTouchEnd = async () => {
      if (!intercepting.current) {
        startX.current = null;
        startY.current = null;
        gestureLocked.current = null;
        return;
      }
      const shouldRefresh = committedRef.current;
      intercepting.current = false;
      startX.current = null;
      startY.current = null;
      committedRef.current = false;
      gestureLocked.current = null;
      if (shouldRefresh) {
        setRefreshing(true);
        // Tiny tick so the spinner gets a chance to paint before the
        // browser blocks on navigation. Without this, on slow connections
        // the spinner looks frozen at the start of the refresh.
        await new Promise((r) => setTimeout(r, 60));
        window.location.reload();
      } else {
        setPull(0);
      }
    };

    // Passive: false on touchmove because we may call preventDefault().
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, refreshing]);

  if (!enabled) return null;

  const committed = pull >= COMMIT_THRESHOLD;
  // Visual progress 0..1 mapped to opacity + icon rotation.
  const progress = Math.min(1, pull / COMMIT_THRESHOLD);
  const visible = refreshing || pull > 4;

  return (
    <div
      aria-hidden
      style={{
        position: "fixed",
        top: "env(safe-area-inset-top)",
        left: 0,
        right: 0,
        height: 0,
        zIndex: 60,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          transform: `translateY(${refreshing ? 24 : Math.max(0, pull - 16)}px)`,
          transition: refreshing || pull === 0 ? "transform 220ms ease" : "none",
          opacity: visible ? 1 : 0,
        }}
      >
        <div
          style={{
            background: "var(--card, white)",
            border: "1px solid hsl(var(--border, 0 0% 90%))",
            borderRadius: 999,
            padding: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          }}
        >
          <RefreshCw
            className="h-4 w-4"
            style={{
              color: committed || refreshing ? "hsl(var(--accent-foreground))" : "hsl(var(--muted-foreground))",
              transform: refreshing
                ? "rotate(0deg)"
                : `rotate(${progress * 270}deg)`,
              transition: "color 120ms ease",
              animation: refreshing ? "spin 800ms linear infinite" : undefined,
            }}
          />
        </div>
      </div>
    </div>
  );
}
